import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  UserPlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../constants/urls';

interface SuperAdmin {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
  last_login_at: string | null;
  is_root_admin?: boolean;
}

interface CreateSuperAdminModal {
  onClose: () => void;
  onSuccess: (newAdmin: SuperAdmin) => void;
}

const CreateSuperAdminModal: React.FC<CreateSuperAdminModal> = ({
  onClose,
  onSuccess,
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    super_admin_secret: '',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/users/super-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create super admin');
      }

      const newAdmin = await response.json();
      onSuccess(newAdmin);
      onClose();
    } catch (err) {
      // console.error removed
      setError(
        err instanceof Error ? err.message : 'Failed to create super admin',
      );
    } finally {
      setCreating(false);
    }
  };

  const generatePassword = () => {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500/75" onClick={onClose} />
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Create Super Admin
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  className="mt-1 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
                >
                  Generate
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Super Admin Secret *
              </label>
              <input
                type="password"
                value={formData.super_admin_secret}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    super_admin_secret: e.target.value,
                  })
                }
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter the super admin secret"
                required
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Super Admin'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const SuperAdminManagement: React.FC = () => {
  const { user, token } = useAuth();
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isRootSuperAdmin, setIsRootSuperAdmin] = useState(false);
  const [rootAdminLoading, setRootAdminLoading] = useState(true);

  // Check if user is root super admin
  useEffect(() => {
    const checkRootAdmin = async () => {
      if (!user || user.role !== 'super_admin') {
        setRootAdminLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/admin/is-root-admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setIsRootSuperAdmin(data.is_root_admin);
        }
      } catch (err) {
        // console.error removed
      } finally {
        setRootAdminLoading(false);
      }
    };

    checkRootAdmin();
  }, [user, token, API_URL]);

  useEffect(() => {
    const fetchSuperAdmins = async () => {
      if (!user || user.role !== 'super_admin') {
        setError('Access denied: Super admin role required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/admin/users?role=super_admin`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch super admins: ${response.statusText}`,
          );
        }

        const allUsers = await response.json();
        // Filter to only super admins
        const admins = allUsers.filter((u: any) => u.role === 'super_admin');
        setSuperAdmins(admins);
      } catch (err) {
        // console.error removed
        setError(
          err instanceof Error ? err.message : 'Failed to fetch super admins',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSuperAdmins();
  }, [user, token, API_URL]);

  const handleCreateSuccess = (newAdmin: SuperAdmin) => {
    setSuperAdmins([...superAdmins, newAdmin]);
  };

  const handleDeleteSuperAdmin = async (
    adminId: string,
    adminEmail: string,
  ) => {
    if (!isRootSuperAdmin) {
      alert('Only the root super admin can delete other super admins');
      return;
    }

    if (adminEmail === user?.email) {
      alert('You cannot delete your own account');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete super admin: ${adminEmail}?`,
      )
    ) {
      return;
    }

    setDeletingId(adminId);
    try {
      const response = await fetch(`${API_URL}/admin/users/${adminId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete super admin');
      }

      setSuperAdmins(superAdmins.filter((admin) => admin.id !== adminId));
    } catch (err) {
      // console.error removed
      alert(
        err instanceof Error ? err.message : 'Failed to delete super admin',
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600">
            This area requires super admin privileges.
          </p>
        </div>
      </div>
    );
  }

  if (loading || rootAdminLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Super Admin Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage super administrator accounts
              {isRootSuperAdmin && (
                <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  <ShieldCheckIcon className="h-3 w-3 mr-1" />
                  Root Admin
                </span>
              )}
            </p>
          </div>
          {isRootSuperAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              <UserPlusIcon className="h-5 w-5 mr-2" />
              Add Super Admin
            </button>
          )}
        </div>
      </div>

      {/* Root Admin Notice */}
      {!isRootSuperAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <span className="text-yellow-800">
              Only the root super admin can create or delete other super admins.
            </span>
          </div>
        </div>
      )}

      {/* Super Admins Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                {isRootSuperAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {superAdmins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-8 w-8 text-red-500 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {admin.first_name && admin.last_name
                            ? `${admin.first_name} ${admin.last_name}`
                            : admin.email}
                          {admin.is_root_admin && (
                            <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Root
                            </span>
                          )}
                          {admin.email === user?.email && (
                            <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              You
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {admin.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Active
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {admin.last_login_at ? (
                      new Date(admin.last_login_at).toLocaleDateString()
                    ) : (
                      <span className="text-gray-400">Never</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                  {isRootSuperAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {admin.email !== user?.email && !admin.is_root_admin && (
                        <button
                          onClick={() =>
                            handleDeleteSuperAdmin(admin.id, admin.email)
                          }
                          disabled={deletingId === admin.id}
                          className="text-red-600 hover:text-red-900 flex items-center disabled:opacity-50"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          {deletingId === admin.id ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {superAdmins.length === 0 && (
          <div className="text-center py-8">
            <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No super admins found.</p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {superAdmins.length}
          </div>
          <div className="text-sm text-gray-500">Total Super Admins</div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSuperAdminModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default SuperAdminManagement;
